/**
 * How far ordinary members take part in a club (setting `member_participation`,
 * chosen by the club admin). One decision for API, demo store, web and app:
 * the API enforces it, the clients only hide what it would refuse.
 *
 *   report  (default) report damages, add photos to own unreviewed reports, write comments
 *   comment            read everything and write comments, but change no damage
 *   read               read only
 *
 * Technicians (`editor`) and admins are never affected.
 */
import { type MemberRole, hasAtLeastRole } from './status';

export const MEMBER_PARTICIPATION_LEVELS = ['report', 'comment', 'read'] as const;
export type MemberParticipation = (typeof MEMBER_PARTICIPATION_LEVELS)[number];

/** A club without the setting behaves as before it existed. */
export const DEFAULT_MEMBER_PARTICIPATION: MemberParticipation = 'report';

/**
 * What a member write is about: `report` = a damage or its photos (create,
 * edit, withdraw, add/remove photos, their undo), `comment` = a comment
 * (write, delete own, their undo).
 */
export type MemberWrite = 'report' | 'comment';

const ALLOWED: Readonly<Record<MemberParticipation, readonly MemberWrite[]>> = {
  report: ['report', 'comment'],
  comment: ['comment'],
  read: [],
};

export function isMemberParticipation(value: unknown): value is MemberParticipation {
  return (
    typeof value === 'string' && (MEMBER_PARTICIPATION_LEVELS as readonly string[]).includes(value)
  );
}

/**
 * May this role write this kind of thing at this level? Only the club setting
 * is decided here; the record rules (own report, unreviewed, …) come on top.
 * An unknown or missing level counts as the default, like a missing row.
 */
export function participationAllows(
  role: MemberRole,
  level: MemberParticipation | null | undefined,
  write: MemberWrite,
): boolean {
  if (role === 'inactive') return false;
  if (hasAtLeastRole(role, 'editor')) return true;
  const effective = isMemberParticipation(level) ? level : DEFAULT_MEMBER_PARTICIPATION;
  return ALLOWED[effective].includes(write);
}
